import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Type,
  Image,
  Square,
  Circle,
  Minus,
  Variable,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Award,
  User,
  Calendar,
  Hash,
  FileText,
  Layers,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  CanvasElementType,
  CertificateTemplateType,
  getDefaultAllowedVariables,
  DEFAULT_TEXT_STYLE
} from '@/types/certificate-template.types';
import certificateTemplateService from '@/services/certificate-template.service';

interface ElementsPanelProps {
  templateType: CertificateTemplateType;
  onAddElement: (elementData: any) => void;
  onInsertVariable?: (variableKey: string) => void;
}

interface ElementCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  items: ElementItem[];
}

interface ElementItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  type: CanvasElementType;
  preset?: string;
  data?: any;
}

export default function ElementsPanel({ templateType, onAddElement, onInsertVariable }: ElementsPanelProps) {
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['variables', 'presets', 'text']);

  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['certificateTemplateVariables', templateType],
    queryFn: () => certificateTemplateService.getTemplatesByType(templateType),
    enabled: !!templateType,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const availableVariables = useMemo(() => {
    if (!templates || templates.length === 0) {
      return getDefaultAllowedVariables(templateType);
    }

    // Combine all unique variables from all templates of this type
    const variablesSet = new Set<string>();
    templates.forEach(template => {
      if (template.allowed_variables) {
        template.allowed_variables.forEach(varName => {
          variablesSet.add(varName);
        });
      }
    });

    const variables = Array.from(variablesSet);
    return variables.length > 0 ? variables : getDefaultAllowedVariables(templateType);
  }, [templates, templateType]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleDragStart = (e: React.DragEvent, item: ElementItem) => {
    const elementData = {
      type: item.type,
      preset: item.preset,
      data: item.data
    };
    e.dataTransfer.setData('application/json', JSON.stringify(elementData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = (item: ElementItem) => {
    // If it's a variable and onInsertVariable is provided, insert into HTML iframe
    if (item.type === CanvasElementType.VARIABLE && onInsertVariable && item.data?.variableKey) {
      onInsertVariable(item.data.variableKey);
      return;
    }
    onAddElement({
      type: item.type,
      preset: item.preset,
      data: item.data
    });
  };

  const categories: ElementCategory[] = [
    {
      id: 'presets',
      name: 'Quick Add',
      icon: <Award className="w-4 h-4" />,
      items: [
        {
          id: 'preset-title',
          name: 'Certificate Title',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          preset: 'title'
        },
        {
          id: 'preset-recipient',
          name: 'Recipient Name',
          icon: <User className="w-4 h-4" />,
          type: CanvasElementType.VARIABLE,
          preset: 'recipientName'
        },
        {
          id: 'preset-subtitle',
          name: 'Subtitle Text',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          preset: 'subtitle'
        },
        {
          id: 'preset-course',
          name: 'Course/Program Name',
          icon: <FileText className="w-4 h-4" />,
          type: CanvasElementType.VARIABLE,
          preset: 'courseName'
        },
        {
          id: 'preset-date',
          name: 'Issue Date',
          icon: <Calendar className="w-4 h-4" />,
          type: CanvasElementType.VARIABLE,
          preset: 'date'
        }
      ]
    },
    {
      id: 'text',
      name: 'Text Elements',
      icon: <Type className="w-4 h-4" />,
      items: [
        {
          id: 'text-heading',
          name: 'Heading',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          data: {
            content: 'Heading Text',
            style: { ...DEFAULT_TEXT_STYLE, fontSize: 28, fontWeight: 'bold' }
          }
        },
        {
          id: 'text-subheading',
          name: 'Subheading',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          data: {
            content: 'Subheading',
            style: { ...DEFAULT_TEXT_STYLE, fontSize: 18, fontWeight: '600' }
          }
        },
        {
          id: 'text-body',
          name: 'Body Text',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          data: {
            content: 'Body text goes here',
            style: { ...DEFAULT_TEXT_STYLE, fontSize: 14 }
          }
        },
        {
          id: 'text-label',
          name: 'Label',
          icon: <Type className="w-4 h-4" />,
          type: CanvasElementType.TEXT,
          data: {
            content: 'LABEL',
            style: { ...DEFAULT_TEXT_STYLE, fontSize: 10, fontWeight: 'bold', letterSpacing: 1 }
          }
        }
      ]
    },
    {
      id: 'variables',
      name: 'Dynamic Variables',
      icon: <Variable className="w-4 h-4" />,
      items: availableVariables.map(varKey => ({
        id: `var-${varKey}`,
        name: varKey,
        icon: getVariableIcon(varKey),
        type: CanvasElementType.VARIABLE,
        data: {
          variableKey: varKey,
          placeholder: `{{${varKey}}}`,  // Show as {{variable_name}}
          format: varKey.includes('date') ? 'date' : 'text'
        }
      }))
    },
    {
      id: 'shapes',
      name: 'Shapes & Lines',
      icon: <Square className="w-4 h-4" />,
      items: [
        {
          id: 'shape-rect',
          name: 'Rectangle',
          icon: <Square className="w-4 h-4" />,
          type: CanvasElementType.SHAPE,
          data: { shapeType: 'rectangle', fill: '#E5E7EB' }
        },
        {
          id: 'shape-circle',
          name: 'Circle',
          icon: <Circle className="w-4 h-4" />,
          type: CanvasElementType.SHAPE,
          data: { shapeType: 'circle', fill: '#E5E7EB' }
        },
        {
          id: 'line-horizontal',
          name: 'Line',
          icon: <Minus className="w-4 h-4" />,
          type: CanvasElementType.LINE,
          data: {
            strokeWidth: 2,
            strokeColor: '#1E3A5F',
            strokeStyle: 'solid',
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 200, y: 0 }
          }
        }
      ]
    },
    {
      id: 'media',
      name: 'Media',
      icon: <Image className="w-4 h-4" />,
      items: [
        {
          id: 'image-logo',
          name: 'Image',
          icon: <Image className="w-4 h-4" />,
          type: CanvasElementType.IMAGE,
          data: { src: '', alt: 'Company Logo', fit: 'contain' }
        }
      ]
    }
  ];

  const visibleCategories = categories;

  return (
    <div className="w-64 bg-white border-r flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-500" />
          Elements
        </h3>
        <p className="text-xs text-gray-500 mt-1">Drag or click to add</p>
      </div>

      {/* Loading indicator for variables */}
      {isLoading && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center gap-2 text-xs text-blue-600">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Loading variables...</span>
          </div>
        </div>
      )}

      {/* Error indicator - using fallback */}
      {error && !isLoading && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
          <div className="flex items-center gap-2 text-xs text-amber-600">
            <AlertCircle className="w-3 h-3" />
            <span>Using default variables</span>
          </div>
        </div>
      )}

      {/* Element Categories */}
      <div className="flex-1 overflow-y-auto">
        {visibleCategories.map((category) => (
          <div key={category.id} className="border-b last:border-b-0">
            <button
              onClick={() => toggleCategory(category.id)}
              className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{category.icon}</span>
                <span className="text-sm font-medium text-gray-700">{category.name}</span>
              </div>
              {expandedCategories.includes(category.id) ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {expandedCategories.includes(category.id) && (
              <div className="px-2 pb-2 space-y-1">
                {category.items.map((item) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onClick={() => handleClick(item)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab hover:bg-gray-100 active:bg-gray-200 transition-colors group"
                  >
                    <GripVertical className="w-3 h-3 text-gray-300 group-hover:text-gray-400" />
                    <span className="text-gray-500">{item.icon}</span>
                    <span className="text-sm text-gray-700 truncate">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Help text */}
      <div className="flex-shrink-0 px-4 py-3 border-t bg-gray-50 text-xs text-gray-500">
        <p className="flex items-center gap-1">
          <span className="font-medium">Tip:</span> Drag elements to canvas or click to add at center
        </p>
      </div>
    </div>
  );
}

// Helper function to get icon based on variable key
function getVariableIcon(varKey: string): React.ReactNode {
  if (varKey.includes('name')) return <User className="w-4 h-4" />;
  if (varKey.includes('date')) return <Calendar className="w-4 h-4" />;
  if (varKey.includes('title') || varKey.includes('course') || varKey.includes('certification') || varKey.includes('learning_path')) return <FileText className="w-4 h-4" />;
  if (varKey.includes('number') || varKey.includes('id') || varKey.includes('score') || varKey.includes('percentage')) return <Hash className="w-4 h-4" />;
  return <Variable className="w-4 h-4" />;
}
